import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  LinearProgress,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  FlightLand,
  MeetingRoom,
} from '@mui/icons-material';
import moment from 'moment';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';
import { calendarContentRevealSx } from '../../utils/dashboardCalendarLoading';
import {
  buildDayStat,
  buildMonthCalendarModel,
  formatLoadPctLabel,
  getDayTrend,
  getLoadPctRaw,
  heatAccentForBar,
  occupancyHeatBackground,
} from './bookingCalendarMonthUtils';

/**
 * Full-month overview. When `inventoryByDate` is set, metrics match /inventory/ daily_stats
 * (booking-item nights sold vs capacity). Also shows arrivals (scheduled check-in date).
 */
const BookingCalendarMonthOverview = ({
  data,
  inventoryByDate = null,
  theme: themeMode = 'light',
  anchorDate,
  isLoading = false,
}) => {
  const isDark = themeMode === 'dark';
  const totalRooms = data?.rooms?.length ?? 0;

  const { weeks, statsByKey, monthLabel } = useMemo(
    () => buildMonthCalendarModel(data, anchorDate, inventoryByDate, totalRooms),
    [data, anchorDate, totalRooms, inventoryByDate],
  );

  const todayStr = moment().format('YYYY-MM-DD');

  const surfaceBorder = isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08);
  const muted = isDark ? alpha('#fff', 0.45) : alpha('#000', 0.45);
  const headingBg = isDark ? alpha(DASHBOARD_ACCENT, 0.07) : alpha(DASHBOARD_ACCENT, 0.12);

  return (
    <Box sx={{ width: '100%', px: { xs: 0.5, sm: 1 }, pb: 2, ...calendarContentRevealSx(isLoading) }}>
      <Box
        sx={{
          mb: 2,
          py: 1.5,
          px: 2,
          borderRadius: 2,
          border: `1px solid ${surfaceBorder}`,
          background: headingBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '0.02em' }}>
          {monthLabel}
        </Typography>
        <Typography variant="caption" sx={{ color: muted, fontWeight: 500 }}>
          {inventoryByDate != null
            ? 'Booked nights & capacity match Inventory insights · arrivals = scheduled check-ins · trends vs prior day'
            : 'Allocated-room occupancy (load inventory data for Inventory-style nights sold)'}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: { xs: 0.5, sm: 1 },
          mb: 1,
        }}
      >
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((wd) => (
          <Typography
            key={wd}
            variant="caption"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: muted,
              fontSize: '0.68rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {wd}
          </Typography>
        ))}
      </Box>

      {weeks.map((week, wi) => (
        <Box
          key={wi}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: { xs: 0.5, sm: 1 },
            mb: { xs: 0.5, sm: 1 },
          }}
        >
          {week.map((day) => {
            const key = day.format('YYYY-MM-DD');
            const inMonth = day.month() === moment(anchorDate).month();
            const snap = statsByKey[key] || buildDayStat(inventoryByDate, data, key, totalRooms);
            const trend = getDayTrend(statsByKey, day, anchorDate, snap);
            const TrendIcon =
              trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : TrendingFlat;
            const trendColor =
              trend === 'up'
                ? isDark
                  ? '#81c784'
                  : '#2e7d32'
                : trend === 'down'
                  ? isDark
                    ? '#e57373'
                    : '#c62828'
                  : muted;

            const isToday = key === todayStr;
            const loadPctRaw = getLoadPctRaw(snap);
            const heatBg = occupancyHeatBackground(loadPctRaw, isDark, inMonth);
            const barClr = heatAccentForBar(loadPctRaw, isDark);
            const barValue = Math.min(100, loadPctRaw);
            const strongHeat = loadPctRaw >= 48;
            const defaultCellBg = isDark
              ? inMonth
                ? alpha('#fff', 0.03)
                : alpha('#fff', 0.01)
              : inMonth
                ? alpha('#fff', 0.85)
                : alpha('#000', 0.02);
            const dayNumColor = !inMonth
              ? muted
              : strongHeat
                ? isDark
                  ? alpha('#fff', 0.96)
                  : '#fff'
                : isDark
                  ? '#fff'
                  : '#111';
            const pctLabelColor = !inMonth
              ? muted
              : strongHeat
                ? isDark
                  ? alpha('#fff', 0.98)
                  : '#fff'
                : isDark
                  ? alpha('#fff', 0.94)
                  : '#111';

            const nightsOrRoomsLabel =
              snap.mode === 'inventory'
                ? `${snap.bookedNights}/${snap.capacity} nights`
                : `${snap.occupied}/${snap.totalRooms} rooms`;

            const cell = (
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: { xs: 108, sm: 124, md: 132 },
                  p: { xs: 0.75, sm: 1 },
                  borderRadius: 2,
                  border: isToday
                    ? `2px solid ${DASHBOARD_ACCENT}`
                    : `1px solid ${surfaceBorder}`,
                  background: heatBg || defaultCellBg,
                  opacity: inMonth ? 1 : 0.42,
                  transition: 'box-shadow 0.2s ease, transform 0.2s ease, background 0.2s ease',
                  boxShadow: isToday
                    ? isDark
                      ? `0 0 20px ${alpha(DASHBOARD_ACCENT, 0.25)}`
                      : `0 4px 14px ${alpha(DASHBOARD_ACCENT, 0.35)}`
                    : 'none',
                  '&:hover': inMonth && !isLoading
                    ? {
                        transform: 'translateY(-2px)',
                        boxShadow: isDark
                          ? `0 8px 24px ${alpha('#000', 0.35)}`
                          : `0 8px 22px ${alpha('#000', 0.08)}`,
                      }
                    : {},
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 0.5,
                    minHeight: 26,
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        fontSize: '0.82rem',
                        color: dayNumColor,
                      }}
                    >
                      {day.format('D')}
                    </Typography>
                    {inMonth && <TrendIcon sx={{ fontSize: 16, color: trendColor, flexShrink: 0 }} aria-hidden />}
                  </Box>
                  {inMonth && loadPctRaw > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '0.88rem', sm: '0.95rem' },
                        lineHeight: 1.1,
                        letterSpacing: '-0.02em',
                        color: pctLabelColor,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      {formatLoadPctLabel(loadPctRaw)}%
                    </Typography>
                  )}
                </Box>

                {inMonth && (
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      mt: 0.5,
                      minHeight: 0,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 0 }}>
                      <FlightLand sx={{ fontSize: 14, color: muted }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.68rem', color: muted }}>
                        {snap.arrivals} arrivals
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.45,
                        mt: 0.35,
                        flexShrink: 0,
                      }}
                    >
                      <MeetingRoom sx={{ fontSize: 18, color: muted }} />
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          fontWeight: 800,
                          fontSize: { xs: '0.82rem', sm: '0.95rem' },
                          letterSpacing: '-0.02em',
                          color: strongHeat
                            ? isDark
                              ? alpha('#fff', 0.92)
                              : alpha('#111', 0.92)
                            : isDark
                              ? alpha('#fff', 0.9)
                              : '#111',
                          lineHeight: 1.2,
                        }}
                      >
                        {nightsOrRoomsLabel}
                      </Typography>
                    </Box>
                    {snap.blockedRooms > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', mt: 0.35, fontSize: '0.62rem', color: muted, flexShrink: 0 }}
                      >
                        +{snap.blockedRooms} blocked (calendar)
                      </Typography>
                    )}
                    <Box sx={{ mt: 'auto', pt: 0.75, flexShrink: 0 }}>
                      <LinearProgress
                        variant="determinate"
                        value={barValue}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            background: `linear-gradient(90deg, ${alpha(barClr, 0.65)} 0%, ${barClr} 100%)`,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            );

            return (
              <Tooltip
                key={key}
                title={
                  inMonth
                    ? snap.mode === 'inventory'
                      ? `${day.format('ddd, MMM D')}: ${snap.arrivals} arrivals · ${snap.bookedNights}/${snap.capacity} booked nights (${snap.bookedPctRaw.toFixed(1)}%) · Direct ${snap.directBooked} · OTA ${snap.onlineBooked}${snap.cancelled ? ` · Cancelled items ${snap.cancelled}` : ''}${snap.blockedRooms ? ` · Blocked ${snap.blockedRooms}` : ''}`
                      : `${day.format('ddd, MMM D')}: ${snap.arrivals} arrivals · ${snap.bookingCount} bookings · ${snap.occupied}/${snap.totalRooms} rooms (${Number(snap.occupancyPctRaw ?? snap.occupancyPct).toFixed(1)}%)${snap.blockedRooms ? ` · ${snap.blockedRooms} blocked` : ''}`
                    : ''
                }
                placement="top"
                arrow
              >
                {cell}
              </Tooltip>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default BookingCalendarMonthOverview;
